import { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  getMetasMes, updateMetas,
  getMetasAgentes, updateMetaAgente,
  getRendimientoAgentes, getPolizasPorRamo, getGraficaActividad, seedDemo,
  exportarReporteExcel, getAgencias, getUsuarios,
  createProspecto, createCita,
} from '../services/api';
import Modal from '../components/ui/Modal';

/* ── helpers ────────────────────────────────────────────────── */
const pct = (a: number, m: number) => m > 0 ? Math.min(Math.round((a / m) * 100), 100) : 0;
const barColor = (p: number) => p >= 100 ? '#6b9b65' : p >= 60 ? '#1d3d6b' : p >= 30 ? '#f59e0b' : '#ef4444';
const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const TIPOS_CITA = ['PrimerContacto','Seguimiento','Cierre','Renovacion','Otro','Personal'];
const TIPO_CITA_LABELS: Record<string, string> = {
  PrimerContacto: 'Primer contacto',
  Seguimiento:    'Seguimiento',
  Cierre:         'Cita de Cierre',
  Renovacion:     'Renovación',
  Otro:           'Otro',
  Personal:       'Personal',
};
const EMPTY_QUICK_CITA = { nombre: '', fechaHora: '', tipo: 'PrimerContacto' };

const RAMO_COLORS = ['#1d3d6b','#6b9b65','#2a509a','#4da6a0','#7e96c9','#a9cba5','#f59e0b','#ef4444'];

/* ── KPI Card ─────────────────────────────────────────────── */
function KpiCard({ label, actual, meta, icon, sub }: any) {
  const p = pct(actual, meta);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
        <span className="text-xl">{icon}</span>
      </div>
      <div className="flex items-end gap-1.5 mb-3">
        <span className="text-3xl font-bold text-gray-900">{actual}</span>
        <span className="text-sm text-gray-400 mb-0.5">/ {meta}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${p}%`, background: barColor(p) }} />
      </div>
      <div className="flex justify-between items-center mt-1.5">
        <p className="text-xs font-semibold" style={{ color: barColor(p) }}>{p}% de meta</p>
        {sub && <p className="text-xs text-gray-400">{sub}</p>}
      </div>
    </div>
  );
}

/* ── Gauge arc ──────────────────────────────────────────────── */
function Gauge({ value, label, color }: { value: number; label: string; color: string }) {
  const r = 54, circ = Math.PI * r;
  const offset = circ - (Math.min(value, 100) / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width={130} height={78} viewBox="0 0 130 80">
        <path d={`M14,70 A${r},${r} 0 0,1 116,70`} fill="none" stroke="#f3f4f6" strokeWidth={12} />
        <path d={`M14,70 A${r},${r} 0 0,1 116,70`} fill="none" stroke={color} strokeWidth={12}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        <text x="65" y="65" textAnchor="middle" fontSize={22} fontWeight="700" fill={color}>{value}%</text>
      </svg>
      <p className="text-xs text-gray-500 -mt-1 text-center">{label}</p>
    </div>
  );
}

/* ── Custom tooltip ─────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-600">{p.name}: <b>{p.value}</b></span>
        </div>
      ))}
    </div>
  );
};

/* ── CSV export ─────────────────────────────────────────────── */
function exportCSV(grafica: any[], agentes: any[], porRamo: any[]) {
  const lines: string[] = [];
  lines.push('ACTIVIDAD MENSUAL (6 meses)');
  lines.push('Mes,Citas,Completadas,Cierres,Llamadas,Prospectos,Convertidos,Pólizas');
  grafica.forEach(g => {
    lines.push([g.mes, g.citas, g.citasCompletadas, g.citasCierre, g.llamadas, g.prospectos, g.convertidos, g.polizas].join(','));
  });
  lines.push('');
  if (porRamo.length) {
    lines.push('PÓLIZAS POR RAMO');
    lines.push('Ramo,Total,Prima Total');
    porRamo.forEach(r => lines.push([r.ramo, r.total, r.primaTotal].join(',')));
    lines.push('');
  }
  if (agentes.length) {
    lines.push('RENDIMIENTO AGENTES');
    lines.push('Agente,Prospectos,Llamadas,Cotizaciones,Citas,Cierres,Convertidos');
    agentes.forEach(a => lines.push([a.nombre, a.prospectos, a.llamadas, a.cotizaciones, a.citas, a.citasCierre, a.convertidos].join(',')));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `reportes-smny-${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

/* ── Main ────────────────────────────────────────────────────── */
export default function Reportes() {
  const now = new Date();
  const [selMes, setSelMes]   = useState(now.getMonth() + 1);
  const [selAnio, setSelAnio] = useState(now.getFullYear());
  const [data, setData]       = useState<any>(null);
  const [porRamo, setPorRamo] = useState<any[]>([]);
  const [agentes, setAgentes] = useState<any[]>([]);
  const [grafica, setGrafica] = useState<any[]>([]);
  const [showMetas, setShowMetas] = useState(false);
  const [seeding, setSeeding]    = useState(false);
  const [seedMsg, setSeedMsg]    = useState('');
  const [metasForm, setMetasForm] = useState({
    metaProspectos: 20, metaLlamadas: 100, metaCitasIniciales: 15,
    metaCitasCierre: 8, metaPolizasVida: 3, metaPolizasTotales: 10,
  });
  const [savingMetas, setSavingMetas] = useState(false);
  const [exportando, setExportando] = useState(false);
  const rol = localStorage.getItem('rol');

  const [metasAgentes, setMetasAgentes] = useState<any[]>([]);
  const [metaAgenteEdit, setMetaAgenteEdit] = useState<{ agenteId: number; nombre: string; form: any } | null>(null);
  const [savingMetaAgente, setSavingMetaAgente] = useState(false);
  const isSuperAdmin = rol === 'SuperAdmin';
  const canSeeAgentes = rol === 'Admin' || isSuperAdmin;
  const [agencias, setAgencias] = useState<any[]>([]);
  const [agentesFiltro, setAgentesFiltro] = useState<any[]>([]);
  const [selAgenciaId, setSelAgenciaId] = useState('');
  const [selAgenteId, setSelAgenteId] = useState('');
  const [showQuickCita, setShowQuickCita] = useState(false);
  const [quickCitaForm, setQuickCitaForm] = useState(EMPTY_QUICK_CITA);
  const [savingQuickCita, setSavingQuickCita] = useState(false);

  const openQuickCita = () => {
    setQuickCitaForm(EMPTY_QUICK_CITA);
    setShowQuickCita(true);
  };

  const submitQuickCita = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingQuickCita(true);
    try {
      const res = await createProspecto({ nombre: quickCitaForm.nombre, telefono: '' });
      await createCita({
        titulo: quickCitaForm.nombre,
        fechaHora: quickCitaForm.fechaHora,
        duracionMinutos: 60,
        tipo: quickCitaForm.tipo,
        prospectoId: res.data.id,
      });
      setShowQuickCita(false);
    } finally {
      setSavingQuickCita(false);
    }
  };

  const DEFAULT_META_AGENTE_FORM = {
    metaProspectos: 20, metaLlamadas: 100, metaCitasIniciales: 15,
    metaCitasCierre: 8, metaPolizasVida: 3, metaPolizasTotales: 10,
    primaEmitidaMeta: 0, primaPagadaMeta: 0,
  };

  const openMetaAgente = (agenteId: number, nombre: string) => {
    const existente = metasAgentes.find(a => a.agenteId === agenteId)?.meta;
    setMetaAgenteEdit({ agenteId, nombre, form: existente ? { ...DEFAULT_META_AGENTE_FORM, ...existente } : { ...DEFAULT_META_AGENTE_FORM } });
  };

  const handleSaveMetaAgente = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!metaAgenteEdit) return;
    setSavingMetaAgente(true);
    try {
      await updateMetaAgente({ agenteId: metaAgenteEdit.agenteId, mes: selMes, anio: selAnio, ...metaAgenteEdit.form });
      setMetaAgenteEdit(null);
      loadAll();
    } finally {
      setSavingMetaAgente(false);
    }
  };

  const handleExportExcel = async () => {
    setExportando(true);
    try {
      const r = await exportarReporteExcel(selMes, selAnio);
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `reporte-${selAnio}-${String(selMes).padStart(2, '0')}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } finally {
      setExportando(false);
    }
  };

  const agenciaIdNum = selAgenciaId ? Number(selAgenciaId) : undefined;
  const agenteIdNum = selAgenteId ? Number(selAgenteId) : undefined;

  const loadAll = () => {
    getMetasMes(selMes, selAnio, agenciaIdNum, agenteIdNum).then(r => {
      setData(r.data);
      if (r.data.metas) setMetasForm(prev => ({ ...prev, ...r.data.metas }));
    });
    getGraficaActividad(6).then(r => setGrafica(r.data));
    if (canSeeAgentes) {
      getPolizasPorRamo(agenciaIdNum).then(r => setPorRamo(r.data));
      getRendimientoAgentes(selMes, selAnio, agenciaIdNum).then(r => setAgentes(r.data));
      getMetasAgentes(selMes, selAnio, agenciaIdNum).then(r => setMetasAgentes(r.data.agentes));
    }
  };

  useEffect(() => {
    if (isSuperAdmin) getAgencias().then(r => setAgencias(r.data));
    if (canSeeAgentes) getUsuarios().then(r => setAgentesFiltro(r.data.filter((u: any) => u.rol === 'Agente')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [selMes, selAnio, selAgenciaId, selAgenteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSeed = async () => {
    setSeeding(true); setSeedMsg('');
    try {
      const r = await seedDemo();
      setSeedMsg(r.data.message);
      loadAll();
    } catch { setSeedMsg('Error al cargar datos de demo.'); }
    finally { setSeeding(false); }
  };

  const handleSaveMetas = async (e: React.FormEvent) => {
    e.preventDefault(); setSavingMetas(true);
    await updateMetas({ ...metasForm, agenciaId: agenciaIdNum });
    setSavingMetas(false); setShowMetas(false); loadAll();
  };

  if (!data) return <div className="flex items-center gap-2 text-gray-400 p-8">
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>Cargando reportes...
  </div>;

  const { metas, porcentajeCierres, pipeline, prospectosMes, llamadasMes,
          citasInicialesMes, citasCierreMes, citasInicialesCompletadasMes, citasCierreCompletadasMes,
          polizasVidaMes, polizasTotalesMes, polizasPagadasMes,
          totalProspectos, convertidos } = data;

  const totalPipeline = pipeline.reduce((a: number, p: any) => a + p.total, 0);
  const tasaCitasCierre = grafica.length
    ? Math.round(grafica.reduce((a: number, g: any) => a + (g.citasCierre || 0), 0)
        / Math.max(grafica.reduce((a: number, g: any) => a + (g.citas || 0), 0), 1) * 100)
    : 0;

  const PIPELINE_ORDER = ['Nuevo','Contactado','Interesado','Cotizado','Convertido','Perdido'];
  const PIPELINE_COLORS: Record<string, string> = {
    Nuevo: '#94a3b8', Contactado: '#60a5fa', Interesado: '#fbbf24',
    Cotizado: '#a78bfa', Convertido: '#6b9b65', Perdido: '#f87171',
  };
  const pieData = PIPELINE_ORDER.map(e => ({
    name: e,
    value: pipeline.find((p: any) => p.estatus === e)?.total ?? 0,
  })).filter(p => p.value > 0);

  return (
    <div className="space-y-6">

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-gray-800">Reportes & Métricas</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select value={selMes} onChange={e => setSelMes(Number(e.target.value))}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-white shadow-sm">
            {MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select value={selAnio} onChange={e => setSelAnio(Number(e.target.value))}
            className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-white shadow-sm">
            {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
          </select>
          {isSuperAdmin && agencias.length > 0 && (
            <select value={selAgenciaId} onChange={e => { setSelAgenciaId(e.target.value); setSelAgenteId(''); }}
              className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-white shadow-sm">
              <option value="">Todas las agencias</option>
              {agencias.map((a: any) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          )}
          {canSeeAgentes && agentesFiltro.length > 0 && (
            <select value={selAgenteId} onChange={e => setSelAgenteId(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-1.5 text-sm bg-white shadow-sm">
              <option value="">Todos los agentes</option>
              {agentesFiltro.map((a: any) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          )}
          <button
            onClick={() => exportCSV(grafica, agentes, porRamo)}
            className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl text-sm hover:bg-gray-50 shadow-sm"
          >
            ⬇️ CSV
          </button>
          <button
            onClick={handleExportExcel}
            disabled={exportando}
            className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl text-sm hover:bg-gray-50 shadow-sm disabled:opacity-60"
          >
            {exportando ? 'Generando...' : '⬇️ Excel'}
          </button>
          <button
            onClick={() => window.print()}
            className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl text-sm hover:bg-gray-50 shadow-sm"
          >
            🖨️ PDF
          </button>
          <button
            onClick={openQuickCita}
            className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl text-sm hover:bg-gray-50 shadow-sm"
          >
            📅 Cita rápida
          </button>
          {(rol === 'Admin' || (isSuperAdmin && selAgenciaId)) && (
            <>
              <button onClick={() => setShowMetas(true)}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl text-sm hover:bg-gray-50 shadow-sm">
                ⚙️ Metas
              </button>
            </>
          )}
          {isSuperAdmin && !selAgenciaId && (
            <span className="text-xs text-gray-400 italic">Elige una agencia para editar metas</span>
          )}
        </div>
        {seedMsg && <p className="w-full text-xs text-accent-600 font-medium">{seedMsg}</p>}
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label="Prospectos del mes"      icon="👥" actual={prospectosMes}      meta={metas.metaProspectos}     sub={`${totalProspectos} total`} />
        <KpiCard label="Llamadas del mes"         icon="📞" actual={llamadasMes}         meta={metas.metaLlamadas} />
        <KpiCard label="Citas iniciales del mes"  icon="🤝" actual={citasInicialesMes}  meta={metas.metaCitasIniciales} sub={`${citasInicialesCompletadasMes} confirmadas`} />
        <KpiCard label="Citas de cierre del mes"  icon="✅" actual={citasCierreMes}     meta={metas.metaCitasCierre} sub={`${citasCierreCompletadasMes} realizadas`} />
        <KpiCard label="Pólizas Vida del mes"     icon="❤️" actual={polizasVidaMes}     meta={metas.metaPolizasVida} />
        <KpiCard label="Pólizas totales del mes"  icon="📄" actual={polizasTotalesMes}  meta={metas.metaPolizasTotales} sub={`${convertidos} convertidos`} />
        <KpiCard label="Pólizas pagadas del mes"  icon="💰" actual={polizasPagadasMes}  meta={Math.max(polizasTotalesMes, 1)} />
      </div>

      {/* ── Gauges row ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Tasa de conversión</p>
          <Gauge value={Math.round(porcentajeCierres)} label={`${convertidos} de ${totalProspectos} prospectos`} color="#6b9b65" />
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Citas de cierre (6m)</p>
          <Gauge value={tasaCitasCierre} label="cierres / citas totales" color="#1d3d6b" />
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Meta prospectos del mes</p>
          <Gauge value={pct(prospectosMes, metas.metaProspectos)} label={`${prospectosMes} de ${metas.metaProspectos}`} color="#f59e0b" />
        </div>
      </div>

      {/* ── Bar chart: actividad 6 meses ─────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-5">Actividad — Últimos 6 meses</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={grafica} margin={{ top: 0, right: 0, bottom: 0, left: -20 }} barGap={2}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 16 }} />
            <Bar dataKey="citas" name="Citas" fill="#1d3d6b" radius={[4,4,0,0]} maxBarSize={32} />
            <Bar dataKey="citasCompletadas" name="Completadas" fill="#6b9b65" radius={[4,4,0,0]} maxBarSize={32} />
            <Bar dataKey="citasCierre" name="Cierres" fill="#f59e0b" radius={[4,4,0,0]} maxBarSize={32} />
            <Bar dataKey="llamadas" name="Llamadas" fill="#a9b9db" radius={[4,4,0,0]} maxBarSize={32} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Line chart: prospectos + pólizas ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-5">Nuevos Prospectos vs Convertidos</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={grafica} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
              <Line dataKey="prospectos" name="Prospectos" stroke="#1d3d6b" strokeWidth={2.5}
                dot={{ fill: '#1d3d6b', r: 4 }} activeDot={{ r: 6 }} />
              <Line dataKey="convertidos" name="Convertidos" stroke="#6b9b65" strokeWidth={2.5}
                dot={{ fill: '#6b9b65', r: 4 }} activeDot={{ r: 6 }} />
              <Line dataKey="polizas" name="Pólizas" stroke="#f59e0b" strokeWidth={2}
                strokeDasharray="5 3" dot={{ fill: '#f59e0b', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* ── Pie: pipeline ─────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Pipeline de Prospectos</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                  paddingAngle={3} dataKey="value">
                  {pieData.map((entry, i) => (
                    <Cell key={entry.name} fill={PIPELINE_COLORS[entry.name] || '#ccc'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: any, n: any) => [v, n]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {pieData.map(p => (
                <div key={p.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIPELINE_COLORS[p.name] }} />
                    <span className="text-xs text-gray-600">{p.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold text-gray-800">{p.value}</span>
                    <span className="text-xs text-gray-400 ml-1">({totalPipeline > 0 ? Math.round(p.value / totalPipeline * 100) : 0}%)</span>
                  </div>
                </div>
              ))}
              {pieData.length === 0 && <p className="text-xs text-gray-400">Sin prospectos</p>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Pólizas por ramo (Admin / SuperAdmin) ────────────────── */}
      {canSeeAgentes && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Pólizas Activas por Ramo</h3>
          {porRamo.length === 0 ? <p className="text-gray-400 text-sm">Sin datos</p> : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={porRamo} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="ramo" width={110} tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" name="Pólizas" radius={[0,4,4,0]} maxBarSize={18}>
                    {porRamo.map((_: any, i: number) => <Cell key={i} fill={RAMO_COLORS[i % RAMO_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-gray-400 mt-2 text-right">
                Prima total: ${porRamo.reduce((a, r) => a + r.primaTotal, 0).toLocaleString('es-MX')}
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Rendimiento Agentes (ancho completo, filtrable por agente) ── */}
      {canSeeAgentes && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Rendimiento Agentes — {MONTHS[selMes - 1]} {selAnio}
          </h3>
          {(() => {
            const agentesFiltrados = selAgenteId
              ? agentes.filter(a => String(a.id) === selAgenteId)
              : agentes;
            if (agentesFiltrados.length === 0) return <p className="text-gray-400 text-sm">Sin datos</p>;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {agentesFiltrados.map(a => {
                  const conv = a.prospectos > 0 ? Math.round((a.convertidos / a.prospectos) * 100) : 0;
                  const bar = barColor(conv >= 50 ? 100 : conv >= 25 ? 65 : 20);
                  return (
                    <div key={a.id} className="border border-gray-100 rounded-xl p-3">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-medium text-gray-800">{a.nombre}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold" style={{ color: bar }}>{conv}% conv.</span>
                          <button onClick={() => openMetaAgente(a.id, a.nombre)}
                            className="text-[11px] px-2 py-0.5 rounded-full border border-primary-200 text-primary-700 hover:bg-primary-50">
                            🎯 {metasAgentes.find(ma => ma.agenteId === a.id)?.meta ? 'Meta' : '+ Meta'}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-1 text-center">
                        {[
                          { label: 'Prosp.', value: a.prospectos },
                          { label: 'Llamadas', value: a.llamadas },
                          { label: 'Cotiz.', value: a.cotizaciones },
                          { label: 'Citas', value: a.citas },
                          { label: 'Cierres', value: a.citasCierre },
                        ].map(m => (
                          <div key={m.label} className="bg-gray-50 rounded-lg py-1.5">
                            <p className="text-sm font-bold text-gray-800">{m.value}</p>
                            <p className="text-[9px] text-gray-400 uppercase">{m.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${conv}%`, background: bar }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Edit Metas Modal ──────────────────────────────────────── */}
      {showMetas && (
        <Modal title="Configurar metas mensuales" onClose={() => setShowMetas(false)} size="sm">
          <form onSubmit={handleSaveMetas} className="space-y-3">
            {[
              ['metaProspectos',     '👥 Prospectos del mes'],
              ['metaLlamadas',       '📞 Llamadas del mes'],
              ['metaCitasIniciales', '🤝 Citas iniciales del mes'],
              ['metaCitasCierre',    '✅ Citas de cierre del mes'],
              ['metaPolizasVida',    '❤️ Pólizas Vida del mes'],
              ['metaPolizasTotales', '📄 Pólizas totales del mes'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <label className="text-sm text-gray-700 flex-1">{label}</label>
                <input type="number" min={0}
                  value={(metasForm as any)[key]}
                  onChange={e => setMetasForm(f => ({ ...f, [key]: Number(e.target.value) }))}
                  className="w-20 border border-gray-300 rounded-xl px-2 py-1.5 text-sm text-center" />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-3 border-t border-gray-100">
              <button type="button" onClick={() => setShowMetas(false)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={savingMetas}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 disabled:opacity-60">
                {savingMetas ? 'Guardando...' : 'Guardar metas'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Edit Meta Individual de Agente Modal ─────────────────────── */}
      {metaAgenteEdit && (
        <Modal title={`Meta de ${metaAgenteEdit.nombre} — ${MONTHS[selMes - 1]} ${selAnio}`} onClose={() => setMetaAgenteEdit(null)} size="sm">
          <form onSubmit={handleSaveMetaAgente} className="space-y-3">
            {[
              ['metaProspectos',     '👥 Prospectos del mes'],
              ['metaLlamadas',       '📞 Llamadas del mes'],
              ['metaCitasIniciales', '🤝 Citas iniciales del mes'],
              ['metaCitasCierre',    '✅ Citas de cierre del mes'],
              ['metaPolizasVida',    '❤️ Pólizas Vida del mes'],
              ['metaPolizasTotales', '📄 Pólizas totales del mes'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <label className="text-sm text-gray-700 flex-1">{label}</label>
                <input type="number" min={0}
                  value={metaAgenteEdit.form[key]}
                  onChange={e => setMetaAgenteEdit(prev => prev && ({ ...prev, form: { ...prev.form, [key]: Number(e.target.value) } }))}
                  className="w-20 border border-gray-300 rounded-xl px-2 py-1.5 text-sm text-center" />
              </div>
            ))}
            <div className="pt-2 border-t border-gray-100" />
            {[
              ['primaEmitidaMeta', '💵 Prima emitida (MXN)'],
              ['primaPagadaMeta',  '💰 Prima pagada (MXN)'],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-3">
                <label className="text-sm text-gray-700 flex-1">{label}</label>
                <input type="number" min={0}
                  value={metaAgenteEdit.form[key]}
                  onChange={e => setMetaAgenteEdit(prev => prev && ({ ...prev, form: { ...prev.form, [key]: Number(e.target.value) } }))}
                  className="w-32 border border-gray-300 rounded-xl px-2 py-1.5 text-sm text-center" />
              </div>
            ))}
            <div className="flex gap-2 justify-end pt-3 border-t border-gray-100">
              <button type="button" onClick={() => setMetaAgenteEdit(null)}
                className="px-4 py-2 border rounded-xl text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="submit" disabled={savingMetaAgente}
                className="px-4 py-2 bg-primary-600 text-white rounded-xl text-sm hover:bg-primary-700 disabled:opacity-60">
                {savingMetaAgente ? 'Guardando...' : 'Guardar meta'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showQuickCita && (
        <Modal title="Agendar cita" onClose={() => setShowQuickCita(false)}>
          <form onSubmit={submitQuickCita} className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
              <input type="text" required value={quickCitaForm.nombre}
                onChange={e => setQuickCitaForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Nombre de la persona a citar"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha y hora *</label>
              <input type="datetime-local" required value={quickCitaForm.fechaHora}
                onChange={e => setQuickCitaForm(f => ({ ...f, fechaHora: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de cita</label>
              <select value={quickCitaForm.tipo}
                onChange={e => setQuickCitaForm(f => ({ ...f, tipo: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                {TIPOS_CITA.map(t => <option key={t} value={t}>{TIPO_CITA_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" onClick={() => setShowQuickCita(false)}
                className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" disabled={savingQuickCita}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 disabled:opacity-60">
                {savingQuickCita ? 'Guardando...' : 'Agendar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
